/// Soundtrack engine — orchestrator over a single playback channel.
///
/// Architecture: at any time the AudioManager plays AT MOST ONE
/// stream. FX cues do NOT overlay the soundtrack — they REPLACE it:
///   1. Soundtrack fades out
///   2. FX fades in, plays out, fades out
///   3. The previous soundtrack URL resumes (fade in)
///
/// Single-channel model — cues and soundtrack share the same audio
/// stream, so one volume control governs everything. No separate FX
/// volume / toggle. Mute (volume = 0) silences both.

import type { AudioManager, AudioStreamHandle } from './audio-manager';
import { getAudioManager } from './audio-manager';

export interface SoundtrackCue {
  url: string;
  loop?: boolean;
  fadeMs?: number;
}

export interface SoundtrackManifest {
  version: number;
  defaultFadeMs?: number;
  cues: Record<string, SoundtrackCue>;
}

export interface PlaySoundtrackOptions {
  loop?: boolean;
  fadeMs?: number;
}

export interface PlayFxOptions {
  /// Override the default cue fade-in (CUE_FADE_MS).
  fadeInMs?: number;
  /// Override the default cue tail fade-out near natural end (CUE_FADE_MS).
  fadeOutMs?: number;
  /// Override volume for this one-shot.
  volume?: number;
}

const VOLUME_KEY = 'polymyth.audio.volume.v3';
const AUDIO_ENABLED_KEY = 'polymyth.audio.enabled.v1';

/// ── Fade durations — client-owned, two settings ────────────────────
/// Fades are driven ENTIRELY by these two client-side defaults, never by
/// the server. Audio WS events (`soundtrack_play` / `soundtrack_stop` /
/// `fx_play`) still carry fade fields, but the client intentionally
/// IGNORES them (see audio-routing.ts) — they're reserved for possible
/// future server-driven, per-cue fade control. One knob per audio type:
///   • SOUNDTRACK — slow, scene-change feel: play fade-in, stop fade-out,
///     and the loop-boundary self-crossfade.
///   • CUE (FX)   — quick punctuation: FX fade-in, FX tail fade-out, and
///     the soundtrack resume-fade-in after an FX ends.
const SOUNDTRACK_FADE_MS = 5000;
const CUE_FADE_MS = 1000;
/// Silence fade — used when the master gate goes OFF or volume hits 0.
/// Short + click-free so "Audio Playback: off" is perceptually instant
/// (NOT the long soundtrack fade, which would keep audio audible for
/// seconds after off and could overlap a re-enabled stream).
const MUTE_FADE_MS = 200;

function readStoredNumber(key: string, fallback: number): number {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const v = Number(raw);
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : fallback;
  } catch {
    return fallback;
  }
}

function readStoredFlag(key: string, fallback: boolean): boolean {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    return raw === '1';
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* localStorage can fail in private mode / quota — silently ignore */
  }
}

type ChannelMode = 'silent' | 'soundtrack' | 'fx';

export class SoundtrackEngine {
  private audioManager: AudioManager;
  private stream: AudioStreamHandle | null = null;
  private mode: ChannelMode = 'silent';
  /// What soundtrack SHOULD be playing when no FX is in progress.
  /// Updated every `playSoundtrack` call; consulted by the FX-end
  /// resume path so continuity is preserved.
  private intendedSoundtrackUrl: string | null = null;
  private intendedSoundtrackLoop = true;

  private volume: number;
  /// Global audio on/off — distinct from volume. When false, NOTHING
  /// plays regardless of volume; flipping it back on resumes the
  /// intended soundtrack. Lets users kill audio without zeroing volume.
  private audioOn: boolean;
  private listeners = new Set<() => void>();

  constructor(audioManager?: AudioManager) {
    this.audioManager = audioManager ?? getAudioManager();
    this.volume = readStoredNumber(VOLUME_KEY, 1.0);
    this.audioOn = readStoredFlag(AUDIO_ENABLED_KEY, true);
  }

  // ── State accessors ─────────────────────────────────────────────

  getVolume(): number {
    return this.volume;
  }
  isEnabled(): boolean {
    return this.audioOn && this.volume > 0;
  }
  isAudioOn(): boolean {
    return this.audioOn;
  }
  /// Channel is silent when audio is globally off OR volume is zero.
  private isSilenced(): boolean {
    return !this.audioOn || this.volume <= 0;
  }
  getCurrentSoundtrackUrl(): string | null {
    return this.intendedSoundtrackUrl;
  }

  // ── Server-driven playback ──────────────────────────────────────

  /// Play (or update) the looping background soundtrack from `url`.
  ///
  /// If an FX is currently mid-play, this updates the intended
  /// soundtrack target (resume url) without interrupting the FX —
  /// when the FX naturally ends, the new soundtrack will fade in.
  /// To force-stop the FX and switch immediately, call `stopSoundtrack`
  /// followed by `playSoundtrack`.
  ///
  /// Idempotent on same URL: re-calls with the same intended URL
  /// while soundtrack mode are no-ops.
  playSoundtrack(url: string, opts?: PlaySoundtrackOptions): void {
    const loop = opts?.loop ?? true;
    const fadeMs = opts?.fadeMs ?? SOUNDTRACK_FADE_MS;

    this.intendedSoundtrackUrl = url;
    this.intendedSoundtrackLoop = loop;

    if (this.mode === 'fx') {
      // FX is playing — when it ends, the resume path picks up the
      // updated intendedSoundtrackUrl.
      this.notify();
      return;
    }

    // Same URL already playing? No-op. Server re-sends `soundtrack_play`
    // on every reconnect (so the new player gets the cue) and the
    // broadcast hits all connections — without this guard each reconnect
    // would restart + crossfade the same URL on every other player.
    if (this.mode === 'soundtrack' && this.stream?.alive && this.stream.url === url) {
      this.notify();
      return;
    }

    if (this.isSilenced()) {
      // Silenced (audio off or volume 0) — fade out any existing stream,
      // do not start a new one. Intended target stays for resume.
      if (this.stream) {
        this.audioManager.stop(this.stream, fadeMs);
        this.stream = null;
      }
      this.mode = 'silent';
      this.notify();
      return;
    }

    const prev = this.stream;
    this.stream = this.audioManager.play({
      url,
      volume: this.volume,
      fadeInMs: fadeMs,
      loop,
      loopCrossfadeMs: fadeMs,
      crossfadeWith: prev,
    });
    this.mode = 'soundtrack';
    this.notify();
  }

  /// Fade out the soundtrack channel and clear the intended target.
  /// FX in flight is also stopped. After this call, nothing plays
  /// until `playSoundtrack` or `playFx` is called again.
  stopSoundtrack(fadeMs: number = SOUNDTRACK_FADE_MS): void {
    this.intendedSoundtrackUrl = null;
    if (this.stream) {
      this.audioManager.stop(this.stream, fadeMs);
      this.stream = null;
    }
    this.mode = 'silent';
    this.notify();
  }

  /// Play a one-shot FX cue. Currently-playing soundtrack fades out,
  /// FX fades in + plays + fades out, then the intended soundtrack
  /// resumes (fade in). If FX is disabled, no-op.
  ///
  /// FX-during-FX: replaces the current FX (cancels its resume
  /// callback — the new FX's resume will pick up the same intended
  /// soundtrack target).
  playFx(url: string, opts?: PlayFxOptions): void {
    if (this.isSilenced()) return;

    const fadeInMs = opts?.fadeInMs ?? CUE_FADE_MS;
    const fadeOutMs = opts?.fadeOutMs ?? CUE_FADE_MS;
    const volume = opts?.volume ?? this.volume;

    const prev = this.stream;
    let handle: AudioStreamHandle | null = null;
    handle = this.audioManager.play({
      url,
      volume,
      fadeInMs,
      loop: false,
      fadeOutAtEnd: { fadeOutMs },
      crossfadeWith: prev,
      // FX-during-FX: if this FX got replaced before naturally ending,
      // its onEnd must not clobber engine state for the newer FX. Bind
      // to the handle and gate on stream identity.
      onEnd: () => {
        if (this.stream === handle) this.handleFxEnd();
      },
    });
    this.stream = handle;
    this.mode = 'fx';
    this.notify();
  }

  /// Called by AudioManager when an FX stream ends naturally. Resumes
  /// the intended soundtrack with a QUICK fade-in (matches the FX
  /// fade-out so the cue feels like a punctuation, not a scene change).
  private handleFxEnd(): void {
    this.stream = null;
    this.mode = 'silent';
    if (this.intendedSoundtrackUrl) {
      const url = this.intendedSoundtrackUrl;
      const loop = this.intendedSoundtrackLoop;
      this.intendedSoundtrackUrl = null;
      // Resume uses the quick CUE fade — not the long soundtrack
      // scene-change fade — so the cue feels like punctuation rather
      // than a scene change.
      this.playSoundtrack(url, { loop, fadeMs: CUE_FADE_MS });
    } else {
      this.notify();
    }
  }

  // ── Settings ────────────────────────────────────────────────────

  setVolume(v: number): void {
    const clamped = Math.min(1, Math.max(0, v));
    const wasSilenced = this.isSilenced();
    this.volume = clamped;
    writeStored(VOLUME_KEY, String(clamped));
    this.applySilenceChange(wasSilenced);
    this.notify();
  }

  /// Global audio on/off. Turning it off silences the channel
  /// immediately (intended soundtrack preserved); turning it back on
  /// resumes that soundtrack — mirroring volume mute/unmute.
  setAudioOn(on: boolean): void {
    if (on === this.audioOn) return;
    const wasSilenced = this.isSilenced();
    this.audioOn = on;
    writeStored(AUDIO_ENABLED_KEY, on ? '1' : '0');
    this.applySilenceChange(wasSilenced);
    this.notify();
  }

  /// Reconcile playback after `audioOn` or `volume` changed.
  /// `wasSilenced` is the silence state BEFORE the change.
  private applySilenceChange(wasSilenced: boolean): void {
    if (this.isSilenced()) {
      // Now silent — sweep ALL active streams (including any outgoing
      // crossfade handle that was dropped from `this.stream` when the
      // new track started) so mid-crossfade audio-off is a true kill
      // switch. Keep the intended target so a later unmute / enable
      // resumes it. (night-watch: bug #1)
      this.audioManager.stopAll(MUTE_FADE_MS);
      this.stream = null;
      this.mode = 'silent';
    } else if (wasSilenced) {
      // Became audible — resume the intended soundtrack with a short
      // fade-in (symmetric with the mute-out) so unmute / re-enable is
      // snappy rather than a multi-second ramp.
      if (this.intendedSoundtrackUrl) {
        const url = this.intendedSoundtrackUrl;
        const loop = this.intendedSoundtrackLoop;
        this.intendedSoundtrackUrl = null;
        this.playSoundtrack(url, { loop, fadeMs: MUTE_FADE_MS });
      }
    } else if (this.stream) {
      // Still audible — live-adjust the running volume.
      this.audioManager.setVolume(this.stream, this.volume, 100);
    }
  }

  // ── Reactivity ──────────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}

let _instance: SoundtrackEngine | null = null;
export function getSoundtrackEngine(): SoundtrackEngine {
  if (!_instance) _instance = new SoundtrackEngine();
  return _instance;
}

export function __setSoundtrackEngineForTesting(engine: SoundtrackEngine | null): void {
  _instance = engine;
}
